import React from 'react';
//import PropTypes from 'prop-types';

// @material-ui
import { DialogTitle, DialogContent } from '@material-ui/core';

// core
import { useGlobal } from '_core/hooks/useGlobal';
import { useSearchTabs } from '_core/hooks/useSearchTabs';
import { withCatch } from '_core/hocs/withCatch';
import { Tabs } from '_core/components/Tabs';
import { ModalOuter } from '_core/components/ModalOuter';

// style
import { useProfileStyle } from './profileStyle';

const getIsVerificationLocked = ({
  verification,
  verificationType,
  verificationStatus,
}) => {
  if (verification == null || verification.constructor !== Object) {
    return false;
  }

  const types = Object.values(verificationType);

  return Object.keys(verification.types || {}).some(
    (key) =>
      types.includes(key) &&
      verification.types[key].is_required &&
      verification.types[key].state !== verificationStatus.SUCCESS
  );
};

const RawProfile = (props) => {
  const classes = useProfileStyle();
  
  const {
    getSlot,
    config,
    request
  } = useGlobal();
  
  const [state, setState] = React.useState({
    isOpen: false,
    isVerificationStatusLoaded: false,
    isVerificationLocked: false,
    tabs: getSlot('profileTabs').map((item) => ({ ...item, state: 'loading' }))
  });
  
  state.tabs.forEach((item) => {
    const getVisibility = item.ContentComponent.getVisibility;
    
    const tabState = typeof getVisibility === 'function'
      ? getVisibility({ ...props, config, request, state, setState, tab: item })
      : 'visible';
    
    if (item.state !== tabState) {
      setTimeout(() => setState((state) => {
        state = { ...state };
        
        item.state = tabState;
        
        return state;
      }), 1);
    }
    
    return item;
  });
  
  const {
    API_METHOD,
    PROFILE_TAB_URL,
    VERIFICATION_STATUS,
    VERIFICATION_TYPE,
    VERIFICATION_SPLASH_CAN_POSTPONE,
    DISABLE_SHOW_PROFILE,
  } = config;
  
  const visibleTabs = DISABLE_SHOW_PROFILE
    ? []
    : state.tabs.filter((tab) => tab.state === 'visible');
  
  const { tab, onTabChange } = useSearchTabs({
    tabs: visibleTabs,
    param: config.ROUTE_URL_PARAM.PROFILE,
    canBeBlank: true
  });
  
  React.useLayoutEffect(() => setState((state) => ({ ...state, isOpen: !!tab })), [tab]);

  React.useEffect(() => {
    if (!state.isOpen) {
      setState((state) => ({
        ...state,
        isVerificationStatusLoaded: false,
        isVerificationLocked: false,
      }));
      return;
    }

    if (VERIFICATION_SPLASH_CAN_POSTPONE) {
      setState((state) => ({
        ...state,
        isVerificationStatusLoaded: true,
        isVerificationLocked: false,
      }));
      return;
    }

    let isMounted = true;

    request.queue(API_METHOD.VISITOR_VERIFICATION)
      .then((verification) => {
        if (!isMounted) {
          return;
        }

        const isVerificationLocked = getIsVerificationLocked({
          verification,
          verificationType: VERIFICATION_TYPE,
          verificationStatus: VERIFICATION_STATUS,
        });

        setState((state) => ({
          ...state,
          isVerificationStatusLoaded: true,
          isVerificationLocked,
        }));
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setState((state) => ({
          ...state,
          isVerificationStatusLoaded: true,
          isVerificationLocked: false,
        }));
      });

    return () => {
      isMounted = false;
    };
  }, [
    API_METHOD.VISITOR_VERIFICATION,
    VERIFICATION_SPLASH_CAN_POSTPONE,
    VERIFICATION_STATUS,
    VERIFICATION_TYPE,
    request,
    state.isOpen,
  ]);

  React.useEffect(() => {
    if (
      !VERIFICATION_SPLASH_CAN_POSTPONE &&
      state.isVerificationLocked &&
      tab != null &&
      tab !== PROFILE_TAB_URL.VERIFICATION
    ) {
      onTabChange(PROFILE_TAB_URL.VERIFICATION);
    }
  }, [
    PROFILE_TAB_URL.VERIFICATION,
    VERIFICATION_SPLASH_CAN_POSTPONE,
    onTabChange,
    state.isVerificationLocked,
    tab,
  ]);

  const isCloseBlocked =
    !VERIFICATION_SPLASH_CAN_POSTPONE &&
    state.isOpen &&
    (!state.isVerificationStatusLoaded || state.isVerificationLocked);
  
  const onClose = () => {
    if (isCloseBlocked) {
      return;
    }

    setState((state) => ({ ...state, isOpen: false }));
  };

  const onVerificationChange = React.useCallback((verification) => {
    setState((state) => ({
      ...state,
      isVerificationStatusLoaded: true,
      isVerificationLocked: getIsVerificationLocked({
        verification,
        verificationType: VERIFICATION_TYPE,
        verificationStatus: VERIFICATION_STATUS,
      }),
    }));
  }, [VERIFICATION_STATUS, VERIFICATION_TYPE]);
  
  return (
    <ModalOuter
      open={state.isOpen}
      closeIcon={!isCloseBlocked}
      disableBackdropClick={isCloseBlocked}
      disableEscapeKeyDown={isCloseBlocked}
      onClose={onClose}
      onExited={() => onTabChange()}
    >
      <Tabs
        value={tab}
        items={visibleTabs}
        onChange={(event, nextTab) => {
          if (isCloseBlocked && nextTab !== PROFILE_TAB_URL.VERIFICATION) {
            return;
          }

          onTabChange(nextTab);
        }}
        contentProps={{ onClose, onVerificationChange }}
        renderTabs={({ tabs }) => (
          <DialogTitle className={classes.title}>{tabs}</DialogTitle>
        )}
        renderTabContents={({ contents }) => (
          <DialogContent>{contents}</DialogContent>
        )}
      />
    </ModalOuter>
  );
};

RawProfile.defaultProps = {};

RawProfile.propTypes = {
};

export const Profile = withCatch(RawProfile);
